/**
 * Email verification landing — `/verify-email/:token`.
 *
 * Calls verify once per token (ref guard). Strict Mode remounts and auth
 * session updates must not re-fire the API or flash a false failure after
 * a successful first request.
 */
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { useAuth } from "../auth";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { getApiClient } from "../services/apiClientProvider";

type State =
	| { kind: "loading" }
	| { kind: "success" }
	| { kind: "error"; message: string };

export function VerifyEmailPage() {
	const { token } = useParams<{ token: string }>();
	const { actions, status } = useAuth();
	const [state, setState] = useState<State>({ kind: "loading" });
	/** Ensures at most one in-flight/completed verify per mounted token. */
	const startedForToken = useRef<string | null>(null);

	useEffect(() => {
		if (!token) {
			setState({ kind: "error", message: "Missing verification token." });
			return;
		}
		if (startedForToken.current === token) {
			return;
		}
		startedForToken.current = token;

		let cancelled = false;
		void getApiClient()
			.verifyEmail({ token })
			.then(async () => {
				// Best-effort session refresh so emailVerified flips when logged in.
				try {
					if (status === "authenticated") {
						await actions.refresh();
					}
				} catch {
					// Ignore refresh failures — verification itself succeeded.
				}
				if (!cancelled) setState({ kind: "success" });
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setState({
						kind: "error",
						message: err instanceof Error ? err.message : String(err),
					});
				}
			});
		return () => {
			cancelled = true;
		};
		// Only re-run when the path token changes — not when auth status updates.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
	}, [token]);

	return (
		<Box sx={{ maxWidth: 520, mx: "auto", mt: { xs: 2, md: 4 } }}>
			<GlassSurface intensity="strong" depth="raised" sx={{ p: 3 }}>
				<Stack spacing={2}>
					<Typography
						variant="overline"
						sx={{ color: "workspace.green", fontWeight: 700, letterSpacing: "0.1em" }}
					>
						Email verification
					</Typography>
					<Typography component="h1" variant="h5">
						Confirm your email
					</Typography>
					{state.kind === "loading" && (
						<Stack direction="row" spacing={1} alignItems="center">
							<CircularProgress size={20} />
							<Typography>Verifying…</Typography>
						</Stack>
					)}
					{state.kind === "success" && (
						<>
							<Alert severity="success">Your email address is verified.</Alert>
							<Button
								component={RouterLink}
								to={status === "authenticated" ? "/dashboard" : "/login"}
								variant="contained"
								sx={{ alignSelf: "flex-start" }}
							>
								{status === "authenticated" ? "Continue to dashboard" : "Sign in"}
							</Button>
						</>
					)}
					{state.kind === "error" && (
						<>
							<Alert severity="error">{state.message}</Alert>
							<Button
								component={RouterLink}
								to="/login"
								sx={{ alignSelf: "flex-start" }}
							>
								Back to sign in
							</Button>
						</>
					)}
				</Stack>
			</GlassSurface>
		</Box>
	);
}
